import { styled } from "goober";
import { playClick } from "../lib/sounds";
import { Icon } from "./Icon";
import { IconBadge } from "./IconBadge";
import { Text } from "./Text";

type Network = {
  id: string;
  name: string;
  identifier: string;
  configuration: unknown;
};

const Container = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &[data-clickable="true"] {
    cursor: pointer;
    &:hover {
      background: var(--alpha4);
    }
  }
`;

const Info = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

function networkTarget(network: Network): string {
  const config = network.configuration as {
    publicHostname?: string;
    gatewayEndpoint?: string;
  } | null;
  return (
    config?.publicHostname ??
    config?.gatewayEndpoint ??
    (network.identifier === "tunnel" ? "Tunnel" : network.identifier)
  );
}

type Props = {
  networks: readonly Network[];
  onSelect?: (network: Network) => void;
  emptyDescription?: string;
};

export function NetworksList({
  networks,
  onSelect,
  emptyDescription = "Networks available in this context appear here.",
}: Props) {
  if (networks.length === 0) {
    return (
      <Container>
        <Row>
          <IconBadge>
            <Icon name="circle-slash" size={16} />
          </IconBadge>
          <Info>
            <Text variant="text-regular-md" color="var(--text-primary)">
              No networks
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {emptyDescription}
            </Text>
          </Info>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      {networks.map((network) => (
        <Row
          key={network.id}
          data-clickable={onSelect ? "true" : undefined}
          onMouseDown={
            onSelect
              ? () => {
                  playClick();
                  onSelect(network);
                }
              : undefined
          }
        >
          <IconBadge>
            <Icon name="earth" size={16} />
          </IconBadge>
          <Info>
            <Text
              ellipsis
              variant="text-regular-md"
              color="var(--text-primary)"
            >
              {network.name || "Untitled network"}
            </Text>
            <Text ellipsis variant="text-small" color="var(--text-secondary)">
              {networkTarget(network)}
            </Text>
          </Info>
        </Row>
      ))}
    </Container>
  );
}
